import { useNavigate, Link } from 'react-router-dom'
import { ArrowRight, AlertCircle, RefreshCw, MapPin } from 'lucide-react'
import { rankCandidates, loadUserAnswers, hasCompletedQuestionnaire, topScorable } from '../engine/matchEngine'
import { useCandidates } from '../hooks/useCandidates'
import { useAuth } from '../contexts/AuthContext'
import MatchBar from '../components/MatchBar'
import { ballotFor, describeBallot } from '../engine/ballotFilter'
import { selectedDistrictKeys } from '../engine/districtSelection'
import { reviewedCandidatesFor } from '../engine/reviewedBallot'

const partyColors = {
  Democratic:  'bg-blue-100 text-blue-800',
  Republican:  'bg-red-100 text-red-800',
  Independent: 'bg-purple-100 text-purple-800',
}

export default function Results() {
  const navigate = useNavigate()
  const { user, userPrefs } = useAuth()
  const completed = hasCompletedQuestionnaire()
  const { weights, stances } = loadUserAnswers()
  const userState = loadUserAnswers().state || userPrefs?.state || null
  // Written by the address lookup in onboarding (AB#419). Empty until a voter
  // has given an address, which ballotFor reads as "show the whole state, and
  // say so" rather than showing nothing.
  const districtKeys = selectedDistrictKeys(userState, loadUserAnswers().district || userPrefs?.district)

  const { candidates, loading, error: fetchError } = useCandidates(
    userState ? { state: userState } : {}
  )

  if (!completed) {
    return (
      <div className="section max-w-xl text-center">
        <AlertCircle className="h-12 w-12 text-civitas-gold mx-auto mb-4" />
        <h1 className="font-display text-2xl text-civitas-navy mb-3">No results yet</h1>
        <p className="text-civitas-silver mb-6">
          Complete the questionnaire to see your personalized candidate match scores.
        </p>
        <Link to="/questionnaire" className="btn-primary">Take the Questionnaire</Link>
      </div>
    )
  }

  // Narrow to this voter's own contests before ranking. Until now this page
  // queried by state alone, so a voter in VA-01 was ranked against every
  // candidate in Virginia - a list headed by people they cannot vote for. The
  // district was collected during onboarding and used only for display.
  //
  // ballotFor keeps statewide races, which carry no district and would be lost
  // to a key filter, and sets aside candidates it cannot place rather than
  // silently dropping or silently including them. See AB#302, AB#688, AB#698.
  let error = fetchError
  let reviewed = { candidates: [], notice: null }
  try {
    reviewed = reviewedCandidatesFor(candidates, { state: userState, districtKeys })
  } catch {
    error = 'Candidate data is inconsistent. Please try again later.'
  }
  const ballot = ballotFor(reviewed.candidates, { state: userState, districtKeys })
  const onBallot = ballot.filtered ? [...ballot.onBallot, ...ballot.statewide] : ballot.unfiltered

  const ranked = onBallot.length ? rankCandidates(weights, stances, onBallot) : []
  // rankCandidates sorts unscorable candidates last and gives them a null
  // score (AB#698). The top match must therefore be a candidate we can
  // actually score - otherwise "your best match" could be someone the system
  // knows nothing about, which is the defect that card exists to fix.
  // topScorable rather than a hand-rolled find: it also rejects undefined,
  // which this page's own version missed, and it exists precisely so a caller
  // cannot undo the null contract by accident.
  const topMatch = topScorable(ranked)
  const scored = ranked.filter(c => Number.isFinite(c.matchScore))
  const unscored = ranked.filter(c => !Number.isFinite(c.matchScore))

  return (
    <div className="section max-w-4xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-civitas-navy">Your Candidate Matches</h1>
          {/* Say which list this is. "Based on your responses" over a
              state-wide list reads as a ballot when it is not, and a voter has
              no way to tell the difference from the page. */}
          <p className="text-civitas-silver mt-1">
            Based on your questionnaire responses. {describeBallot(ballot)}
          </p>
          {!ballot.filtered && (
            <Link to="/onboarding" className="text-sm text-civitas-navy underline mt-1 inline-block">
              Select your districts by address or manually
            </Link>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-civitas-light rounded-full text-sm font-medium text-civitas-navy">
            <MapPin className="h-3.5 w-3.5 text-civitas-gold" />
            {userState || 'All States'}
          </div>
          <button onClick={() => navigate('/questionnaire')} className="text-xs text-civitas-silver underline hover:text-civitas-navy">
            Change state
          </button>
          <Link to="/questionnaire" className="btn-secondary flex items-center gap-2 text-sm">
            <RefreshCw className="h-3 w-3" /> Retake
          </Link>
        </div>
      </div>

      {!loading && !error && reviewed.notice && <aside className="mb-6 rounded-lg bg-civitas-light p-4" aria-label="Election roster coverage">
        <p>Virginia federal offices - U.S. Senate and U.S. House: November 3, 2026 general election. Roster checked {reviewed.notice.verifiedOn} against the{' '}
          <a className="underline" href={reviewed.notice.sourceUrl} target="_blank" rel="noopener noreferrer">Virginia ELECT roster</a>.
          {' '}State and local contests have not been verified by this roster review; this list may not be your complete ballot.</p>
        {!reviewed.notice.available
          ? <p role="status">This roster is outside its verified election period. Federal matches are unavailable until the roster is updated.</p>
          : reviewed.notice.missing.length > 0 && <p role="status">Candidate data needs review for: {reviewed.notice.missing.join(', ')}. These candidates are not included in the matches below. Check the official roster.</p>}
      </aside>}
      {loading && <div className="text-center py-12 text-civitas-silver animate-pulse">Loading candidates...</div>}

      {error && !loading && (
        <div className="text-center py-12 text-red-500">
          <p className="font-medium">Could not load candidates.</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      )}

      {!loading && !error && ranked.length === 0 && (
        <div className="text-center py-12 text-civitas-silver">
          <p>No candidates found{userState ? ` for ${userState}` : ''}.</p>
          <p className="text-sm mt-2">
            <Link to="/questionnaire" className="text-civitas-blue underline">Update your state in the questionnaire</Link>
          </p>
        </div>
      )}

      {!loading && !error && ranked.length > 0 && (
        <>
          {topMatch ? <div className="bg-civitas-navy text-white rounded-xl p-6 mb-8 flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex-1">
              <p className="text-civitas-gold text-xs font-semibold uppercase tracking-widest mb-1">Your Top Match</p>
              <h2 className="font-display text-2xl font-bold">{topMatch.name}</h2>
              <p className="text-civitas-silver text-sm">{topMatch.state} - {topMatch.district} - {topMatch.office}</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-civitas-gold">
                {topMatch.matchScore === null ? '—' : `${topMatch.matchScore}%`}
              </div>
              <div className="text-xs text-civitas-silver">alignment</div>
            </div>
            <Link to={`/candidates/${topMatch.id}`} className="btn-primary flex items-center gap-2 whitespace-nowrap">
              View Profile <ArrowRight className="h-4 w-4" />
            </Link>
          </div> : <p className="mb-6">There is not enough evidence to recommend a top match. You can still inspect the candidates below.</p>}

          <section className="mb-10">
            <h2 className="font-display text-2xl text-civitas-navy mb-4">
              {userState || 'All'} Candidates
            </h2>
            <div className="flex flex-col gap-4">
              {scored.map((c, i) => (
                <div key={c.id} className="card flex flex-col md:flex-row items-start md:items-center gap-4">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className="text-2xl font-bold text-gray-200 w-8 shrink-0">#{i + 1}</span>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-civitas-navy truncate">{c.name}</h3>
                      <p className="text-xs text-civitas-silver">{c.district} - {c.office}</p>
                      <span className={`badge text-xs mt-1 ${partyColors[c.party] ?? 'bg-gray-100 text-gray-600'}`}>{c.party}</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-[180px] w-full">
                    <MatchBar score={c.matchScore} label="Match" />
                    <p className="text-xs text-civitas-silver">Evidence covers {c.coverage.scored} of {c.coverage.answered} answered questions.</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Link to={`/candidates/${c.id}`} className="btn-secondary text-xs">Profile</Link>
                    <Link to={`/compare?a=${c.id}`} className="btn-primary text-xs">Compare</Link>
                  </div>
                </div>
              ))}
            </div>
          </section>
          {unscored.length > 0 && (
            <section className="mb-10">
              <h2 className="font-display text-2xl text-civitas-navy mb-1">
                Candidates not yet scored
              </h2>
              <p className="text-sm text-civitas-silver mb-4">
                {unscored.length === 1 ? 'This candidate has' : `These ${unscored.length} candidates have`}{' '}
                not enough recorded policy evidence to produce a match score.
                They are left unranked because ranking
                them would imply a comparison we have not made.
              </p>
              <div className="flex flex-col gap-3">
                {unscored.map(c => (
                  <div key={c.id} className="card flex flex-col md:flex-row items-start md:items-center gap-4 border-dashed">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className="text-2xl font-bold text-gray-200 w-8 flex-shrink-0" aria-hidden="true">—</span>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-civitas-navy truncate">{c.name}</h3>
                        <p className="text-xs text-civitas-silver">{c.district} - {c.office}</p>
                        <span className={`badge text-xs mt-1 ${partyColors[c.party] ?? 'bg-gray-100 text-gray-600'}`}>{c.party}</span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-[180px] w-full text-xs text-civitas-silver">
                      Insufficient recorded evidence
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Link to={`/candidates/${c.id}`} className="btn-secondary text-xs">Profile</Link>
                      <Link to={`/compare?a=${c.id}`} className="btn-primary text-xs">Compare</Link>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {!loading && !error && ballot.unplaceable.length > 0 && <section className="mb-6">
        <h2 className="font-semibold">District not verified</h2>
        <p>These candidates are not included in your matches because we cannot verify their district.</p>
        <details className="mt-2">
          <summary className="cursor-pointer">Show {ballot.unplaceable.length} candidates with unverified districts (not matches)</summary>
        {ballot.unplaceable.map(c => <p key={c.id}><Link to={`/candidates/${c.id}`} className="underline">{c.name}</Link> — {c.office}, {c.district}</p>)}
        </details>
      </section>}
      <div className="mt-4 p-4 bg-civitas-light rounded-lg text-xs text-civitas-silver">
        <strong>Privacy note:</strong> {user
          ? 'Match scores are computed in your browser. Your location preferences and questionnaire answers are also saved to your account.'
          : 'No account is required. Your district and questionnaire answers stay in this browser. Clearing browser data removes them. Match scores are computed here without uploading your answers.'}
      </div>
    </div>
  )
}
